import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Users, Ticket, LayoutGrid, MessageCircle, Upload, Check } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "./Reveal";

const guests = [
  { name: "Familia Benítez Rojas", passes: 4, mesa: "Mesa 3", status: "confirmado" },
  { name: "Carla Duarte", passes: 2, mesa: "Mesa 5", status: "pendiente" },
  { name: "Luis y Ana Marecos", passes: 2, mesa: "Mesa 1", status: "confirmado" },
  { name: "Martina Ríos", passes: 1, mesa: "Mesa 7", status: "pendiente" },
  { name: "Diego Ayala", passes: 2, mesa: "Mesa 4", status: "cancelado" },
];

const mesas = [
  { nombre: "Mesa 1", personas: ["Luis Marecos", "Ana Marecos"], libres: 2 },
  { nombre: "Mesa 2", personas: ["Fam. González Vera"], libres: 3 },
  { nombre: "Mesa 3", personas: ["Fam. Benítez Rojas"], libres: 0 },
  { nombre: "Mesa 4", personas: ["Diego Ayala"], libres: 5 },
];

const statusStyle = {
  confirmado: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  pendiente: "bg-amber-50 text-amber-700 ring-amber-200",
  cancelado: "bg-rose-50 text-rose-700 ring-rose-200",
};

const tabs = [
  { key: "invitados", label: "Invitados", icon: Users },
  { key: "pases", label: "Pases", icon: Ticket },
  { key: "mesas", label: "Mesas", icon: LayoutGrid },
  { key: "mensajes", label: "Mensajes", icon: MessageCircle },
];

export const PanelMockup = () => {
  const [tab, setTab] = useState("invitados");

  return (
    <section id="panel" data-testid="panel-section" className="bg-[#FAFBFD] py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal>
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-indigo-600">Organización del evento</p>
          <h2
            className="mt-3 max-w-2xl text-3xl font-medium tracking-tight text-slate-900 sm:text-4xl"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            Invitados, confirmaciones, pases y mesas en un solo panel
          </h2>
          <p className="mt-4 max-w-2xl text-base text-slate-600">
            Dejá atrás las planillas desordenadas. Todo el estado de tu evento, actualizado a medida que tus invitados responden.
          </p>
        </Reveal>

        <Reveal delay={0.15}>
          <div
            data-testid="dashboard-panel-preview"
            className="mt-12 overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-[0_24px_60px_-30px_rgba(15,23,42,0.25)]"
          >
            <div className="flex items-center gap-2 border-b border-slate-100 px-5 py-3">
              <span className="h-2.5 w-2.5 rounded-full bg-rose-300" />
              <span className="h-2.5 w-2.5 rounded-full bg-amber-300" />
              <span className="h-2.5 w-2.5 rounded-full bg-emerald-300" />
              <span className="ml-3 text-xs text-slate-400" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                panel.pro360.com.py · vista de demostración
              </span>
            </div>

            <div className="flex gap-1 overflow-x-auto border-b border-slate-100 px-3 py-2">
              {tabs.map((t) => (
                <button
                  key={t.key}
                  data-testid={`dashboard-tab-${t.key}`}
                  onClick={() => setTab(t.key)}
                  className={`flex shrink-0 items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors duration-300 ${
                    tab === t.key ? "bg-indigo-50 text-indigo-700" : "text-slate-500 hover:text-slate-800"
                  }`}
                >
                  <t.icon className="h-4 w-4" />
                  {t.label}
                </button>
              ))}
            </div>

            <div className="min-h-[320px] p-5 sm:p-8">
              <AnimatePresence mode="wait">
                <motion.div
                  key={tab}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.3 }}
                >
                  {tab === "invitados" && (
                    <div>
                      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                        <p className="text-sm text-slate-500">
                          <span className="font-semibold text-slate-900">5 grupos</span> · 2 confirmados · 2 pendientes · 1 cancelado
                        </p>
                        <button
                          data-testid="dashboard-import-guests"
                          onClick={() => toast("La importación de invitados se realiza desde tu panel real (demo)")}
                          className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-600 transition-colors hover:border-indigo-200 hover:text-indigo-700"
                        >
                          <Upload className="h-3.5 w-3.5" />
                          Importar lista
                        </button>
                      </div>
                      <div className="divide-y divide-slate-100">
                        {guests.map((g) => (
                          <div key={g.name} className="flex flex-wrap items-center gap-3 py-3">
                            <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-800">{g.name}</span>
                            <span className="text-xs text-slate-400">{g.passes} pases · {g.mesa}</span>
                            <span className={`rounded-full px-3 py-1 text-xs font-semibold capitalize ring-1 ${statusStyle[g.status]}`}>
                              {g.status}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {tab === "pases" && (
                    <div className="grid gap-3 sm:grid-cols-2">
                      {guests.map((g) => (
                        <div key={g.name} className="flex items-center justify-between rounded-2xl border border-slate-200 px-5 py-4">
                          <div>
                            <p className="text-sm font-medium text-slate-800">{g.name}</p>
                            <p className="text-xs text-slate-400">{g.mesa}</p>
                          </div>
                          <span className="rounded-full bg-indigo-50 px-3.5 py-1.5 text-sm font-semibold text-indigo-700">
                            {g.passes} {g.passes === 1 ? "pase" : "pases"}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  {tab === "mesas" && (
                    <div data-testid="dashboard-table-planner" className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                      {mesas.map((m) => (
                        <div key={m.nombre} className="rounded-2xl border border-slate-200 p-5">
                          <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full border-2 border-dashed border-indigo-200 bg-indigo-50/50">
                            <span className="text-xs font-semibold text-indigo-700">{m.nombre}</span>
                          </div>
                          <div className="mt-4 space-y-1 text-center">
                            {m.personas.map((p) => (
                              <p key={p} className="truncate text-xs text-slate-600">{p}</p>
                            ))}
                            <p className="text-[11px] text-slate-400">
                              {m.libres === 0 ? "Mesa completa" : `${m.libres} lugares libres`}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {tab === "mensajes" && (
                    <div className="mx-auto max-w-md">
                      <p className="text-sm text-slate-500">
                        Prepará un mensaje individual para cada invitado y envialo por WhatsApp desde tu propio teléfono.
                      </p>
                      <div className="mt-5 rounded-2xl rounded-tl-sm border border-slate-200 bg-slate-50 p-4 text-left">
                        <p className="text-sm text-slate-700">
                          Hola <span className="font-semibold">Familia Benítez Rojas</span> 👋 Les compartimos la invitación a la boda de Sofía & Mateo. Tienen <span className="font-semibold">4 pases</span> asignados. Pueden ver los detalles y confirmar aquí: <span className="text-indigo-600 underline">pro360.com.py/i/demo</span>
                        </p>
                      </div>
                      <button
                        data-testid="dashboard-whatsapp-copy"
                        onClick={() => {
                          navigator.clipboard?.writeText(
                            "Hola Familia Benítez Rojas 👋 Les compartimos la invitación a la boda de Sofía & Mateo. Tienen 4 pases asignados. Pueden ver los detalles y confirmar aquí: pro360.com.py/i/demo"
                          );
                          toast.success("Mensaje copiado, listo para pegar en WhatsApp (demo)");
                        }}
                        className="mt-4 inline-flex items-center gap-2 rounded-full bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-emerald-600"
                      >
                        <Check className="h-4 w-4" />
                        Copiar mensaje individual
                      </button>
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
